import React from 'react';
import PropTypes from 'prop-types';

import { BingMapView } from '@bingads-webui-campaign-react/recommendation-visuals';
import { AdvisorChart } from '@bingads-webui-campaign-react/advisor-chart';
import {
  VISUAL_TYPES,
  withTrack,
  ADINSIGHT_LOG_API_NAME,
  ADINSIGHT_LOG_ACTION_TYPE,
  isCompetition,
  RECOMMENDATION_IDS,
  CHANNEL_TYPES,
} from '@bingads-webui-campaign/recommendation-core';

import { FormActionWithEstimates } from './form-action-with-estimates';
import { UnEditableSimpleForm } from './uneditable-simple-form';
import { GridActionWithEstimates } from './grid-action-with-estimates';

class individualRecommendation extends React.PureComponent {
  static propTypes = {
    actionTitle: PropTypes.string,
    insightTitle: PropTypes.string.isRequired,
    visualType: PropTypes.string.isRequired,
    visualData: PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.objectOf(PropTypes.any),
    ]).isRequired,
    estimates: PropTypes.objectOf(PropTypes.any),
    actionDataInForm: PropTypes.objectOf(PropTypes.any),
    actionDataInGrid: PropTypes.objectOf(PropTypes.any),
    actionTitleExtraForm: PropTypes.arrayOf(PropTypes.object),
    name: PropTypes.string.isRequired,
    deps: PropTypes.shape({
      i18n: PropTypes.PropTypes.shape({
        getString: PropTypes.func.isRequired,
      }).isRequired,
      locationMap: PropTypes.func.isRequired,
    }).isRequired,
    track: PropTypes.func.isRequired,
    type: PropTypes.string.isRequired,
    index: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired,
    currentActivity: PropTypes.objectOf(PropTypes.any).isRequired,
    readOnly: PropTypes.bool.isRequired,
    context: PropTypes.shape({
      Level: PropTypes.string.isRequired,
    }).isRequired,
    guid: PropTypes.string.isRequired, // for adinsight view event log in aria
    logExpInfo: PropTypes.func.isRequired,
  };

  static defaultProps = {
    estimates: null,
    actionDataInForm: null,
    actionDataInGrid: null,
    actionTitleExtraForm: null,
    actionTitle: '',
  };

  constructor() {
    super();
    this.actionRef = React.createRef();
  }

  getUserInputs() {
    return this.actionRef.current.getUserInputs(true, true);
  }

  // this is for adinsight aria listener
  logOpportunityEvent = ({ opportunityId, campaignId }) => {
    let api = ADINSIGHT_LOG_API_NAME.RECOMMENDATION;
    let channel = CHANNEL_TYPES.RECOMMENDATION;
    if (isCompetition(this.props.type)) {
      api = ADINSIGHT_LOG_API_NAME.COMPETITION;
      channel = CHANNEL_TYPES.COMPETITION;
    }

    this.props.currentActivity.trace({
      id: opportunityId,
      type: this.props.type,
      typeId: RECOMMENDATION_IDS[this.props.type],
      action: ADINSIGHT_LOG_ACTION_TYPE.VIEW,
      campaignId,
      channel,
      context: JSON.stringify(this.props.context),
      guid: this.props.guid,
    }, api);
  }

  initializeChart() {
    const {
      visualType,
      visualData,
      deps: { locationMap, i18n },
    } = this.props;

    let chart = null;
    switch (visualType) {
      case VISUAL_TYPES.LINE_WEEKLY_CHART:
      case VISUAL_TYPES.COLUMN_CHART:
      case VISUAL_TYPES.LINE_CHART:
      case VISUAL_TYPES.LINE_COLUMN_CHART:
      case VISUAL_TYPES.SEARCH_TERMS_CHART:
        chart = (<AdvisorChart
          i18n={i18n}
          type="RecommendationCard"
          subType={visualType}
          {...visualData}
        />);
        break;
      case VISUAL_TYPES.TABLE:
        chart = <UnEditableSimpleForm fields={visualData} />;
        break;
      case VISUAL_TYPES.MAP:
        chart = <BingMapView locationMap={locationMap} data={visualData} />;
        break;
      default:
        break;
    }
    return chart;
  }

  render() {
    const {
      deps: { i18n },
      insightTitle,
      actionTitle,
      actionDataInForm,
      estimates,
      actionDataInGrid,
      actionTitleExtraForm,
      name,
      type,
      track,
      currency,
      readOnly,
      context,
      guid,
      logExpInfo,
    } = this.props;

    const gridRowIndex = (this.props.index + 1) * 2;
    const gridSeparatorRowIndex = gridRowIndex + 1;
    const rowClass = `action-details-row-${gridRowIndex}`;
    const separatorRowClass = `action-details-row-${gridSeparatorRowIndex}`;

    const commonOptionsForActionAndEstimates = {
      title: actionTitle,
      i18n,
      track,
      type,
      ref: this.actionRef,
      logOpportunityEvent: this.logOpportunityEvent,
      readOnly,
      context,
      guid,
      logExpInfo,
    };
    let actionAndEstimates = null;
    if (actionDataInGrid) {
      actionAndEstimates = (
        <GridActionWithEstimates
          {...actionDataInGrid}
          name={name}
          key={actionDataInGrid.id}
          {...commonOptionsForActionAndEstimates}
          rowClass={rowClass}
        >
          {actionTitleExtraForm &&
            <UnEditableSimpleForm fields={actionTitleExtraForm} />}
        </GridActionWithEstimates>);
    } else {
      actionAndEstimates = (
        <FormActionWithEstimates
          {...actionDataInForm}
          key={actionDataInForm.id}
          estimates={estimates}
          {...commonOptionsForActionAndEstimates}
          rowClass={rowClass}
          currency={currency}
        />);
    }

    return (
      <React.Fragment>
        <div className={`details-insight-col ${rowClass}`} >
          <strong
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: insightTitle }}
          />
          {this.initializeChart()}
        </div>
        <div className={`separator-col details-separator-col-1 ${rowClass}`} />
        {actionAndEstimates}
        <div className={`details-separator-row ${separatorRowClass}`} />
      </React.Fragment>
    );
  }
}

export const IndividualRecommendation = withTrack(/* withAll */ false)(individualRecommendation);
